/**
 * useBilling Hooks
 * TanStack Query hooks for billing profile and invoices
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingService } from '@/services/api/profile';
import type { UpdateBillingProfileRequest } from '@/types/profile';

export const billingProfileQueryKey = ['billing', 'profile'] as const;
export const invoicesQueryKey = ['billing', 'invoices'] as const;

/**
 * Hook for fetching billing profile
 */
export function useBillingProfile() {
  return useQuery({
    queryKey: billingProfileQueryKey,
    queryFn: () => billingService.getProfile(),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook for updating billing profile
 */
export function useUpdateBillingProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateBillingProfileRequest) => billingService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingProfileQueryKey });
    },
  });
}

/**
 * Hook for fetching paginated invoice list
 */
export function useInvoices(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...invoicesQueryKey, limit, offset],
    queryFn: () => billingService.getInvoices(limit, offset),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook for fetching a single invoice detail
 */
export function useInvoiceDetail(id: string | null) {
  return useQuery({
    queryKey: ['billing', 'invoice', id],
    queryFn: () => billingService.getInvoiceDetail(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 10,
  });
}
